import {Plural, Trans} from '@lingui/macro';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {OkCancelDialog} from '../../../../../../../src/components/common/dialogs/okCancelDialog';
import {NamespaceDropdownBoxContainer} from '../../../../../../../src/components/common/dropdowns/namespace/namespaceDropdownBoxContainer';
import {getNameForCard} from '../../../../../../../src/helpers/models/cardHelpers';
import {globalNamespace, isTeammateNamespace} from '../../../../../../../src/helpers/models/namespaceHelpers';
import {CardGroupModel} from '../../../../../../../src/models/api/cardGroupModel';
import {CardModel} from '../../../../../../../src/models/api/cardModel';
import {PermissionsEnum} from '../../../../../../../src/models/api/roleModel';
import {Handler} from '../../../../../../shared/helpers/src/react/reactHelpers';
import {ButtonVariantsEnum} from '../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {fontWeights} from '../../../../../../shared/ui/src/styles/fontStyles';
import {useContactsManagerContext} from '../contactsManagerContext';
import {
  buildContactsManagerEditCardGroupsEvent,
  ContactsManagerEditCardGroupsTrackingActionsEnum,
} from '../helpers/trackingHelpers';

/*
 * Constants.
 */

const namespaceDropdownHeight = 150;
const namespaceDropdownWidth = 260;

/*
 * Props.
 */

interface OtherCardNamespaceConfirmationDialogProps {
  cards: ReadonlyArray<CardModel>;
  cardGroups: ReadonlyArray<CardGroupModel>;
  onClose: Handler;
  onSuccess?: Handler;
  onAddOtherCardsToGroupsAsync: (
    cardGroups: ReadonlyArray<CardGroupModel>,
    cards: ReadonlyArray<CardModel>,
    namespace: string,
  ) => Promise<void>;
}

/*
 * Styles.
 */

const StyledBoldTextSpan = styled.span`
  font-weight: ${fontWeights.bold};
`;

const StyledNamespaceDropdownWrapperDiv = styled.div`
  width: ${namespaceDropdownWidth}px;
  padding-top: 8px;
`;

const StyledMessageWrapperSpan = styled.span`
  padding-bottom: 9px;
`;

/*
 * Component.
 */

export const OtherCardNamespaceConfirmationDialog: FC<OtherCardNamespaceConfirmationDialogProps> = (
  props,
) => {
  const {cardGroups, cards, onClose, onSuccess, onAddOtherCardsToGroupsAsync} = props;
  const [namespace, setNamespace] = useState(globalNamespace);
  const isAllGroupsPrivate = cardGroups.every((group) => isTeammateNamespace(group.namespace));
  const {onTrackHandler} = useContactsManagerContext();
  return (
    <OkCancelDialog
      title={renderTitle(cardGroups, isAllGroupsPrivate)}
      okButtonLabel={renderButtonLabel(cardGroups)}
      okButtonVariant={ButtonVariantsEnum.PRIMARY}
      onRequestClose={onClose}
      onOkClick={async () => {
        await onAddOtherCardsToGroupsAsync(cardGroups, cards, namespace);
        onTrackHandler(
          buildContactsManagerEditCardGroupsEvent(
            ContactsManagerEditCardGroupsTrackingActionsEnum.ADD,
            cards.length,
          ),
        );
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      }}
    >
      <StyledMessageWrapperSpan>
        {renderMessage(cardGroups, cards, isAllGroupsPrivate)}
      </StyledMessageWrapperSpan>
      {isAllGroupsPrivate && (
        <StyledNamespaceDropdownWrapperDiv>
          <NamespaceDropdownBoxContainer
            selectedNamespace={namespace}
            resourceNamespace={undefined}
            onNamespaceSelect={setNamespace}
            permission={PermissionsEnum.EDIT_CARD}
            isDisplayOnly={false}
            shouldSkipTeamCheck
            label={<Trans id="other-card-namespace-confirmation-dialog-access-label">Contacts access</Trans>}
            header={<Trans id="other-card-namespace-confirmation-dialog-access-header">Access</Trans>}
            maxHeight={namespaceDropdownHeight}
            shouldRenderVisibilityDescription
            shouldRenderCompanyDropdownItem
            isRequired
          />
        </StyledNamespaceDropdownWrapperDiv>
      )}
    </OkCancelDialog>
  );
};

/*
 * Helpers.
 */

function renderTitle(cardGroups: ReadonlyArray<CardGroupModel>, isAllGroupsPrivate: boolean) {
  if (isAllGroupsPrivate) {
    return (
      <Plural
        id="other-card-namespace-confirmation-dialog-private-groups-title"
        value={cardGroups.length}
        one={`Add to "${cardGroups[0].name}"`}
        other="Add to private or company contacts"
      />
    );
  }

  return <Trans id="other-card-namespace-confirmation-dialog-title">Add to Company contacts</Trans>;
}

function renderButtonLabel(cardGroups: ReadonlyArray<CardGroupModel>) {
  return (
    <Plural
      id="other-card-namespace-confirmation-dialog-button-label"
      value={cardGroups.length}
      one="Add to list"
      other="Add to lists"
    />
  );
}

function renderMessage(
  cardGroups: ReadonlyArray<CardGroupModel>,
  cards: ReadonlyArray<CardModel>,
  isAllGroupsPrivate: boolean,
) {
  if (isAllGroupsPrivate) {
    return (
      <>
        {renderCardNameOrCount(cards)}
        <Plural
          id="other-card-namespace-confirmation-dialog-message-private-group-part-1"
          value={cards.length}
          one="hasn't"
          other="haven't"
        />
        &nbsp;
        <Trans id="other-card-namespace-confirmation-dialog-private-group-message-part-2">
          been added to Company or Private contacts yet. To add them to a list, please choose the access these
          contacts should have.
        </Trans>
      </>
    );
  }
  return (
    <>
      {renderCardNameOrCount(cards)}
      <Plural
        id="other-card-namespace-confirmation-dialog-message"
        value={cards.length}
        one={`is not a Company contact yet. Adding them to "${cardGroups[0].name}" will make them a Company contact.`}
        other="are not Company contacts yet. Adding them to these lists will make them Company contacts."
      />
    </>
  );
}

function renderCardNameOrCount(cards: ReadonlyArray<CardModel>) {
  return (
    <StyledBoldTextSpan>
      <Plural
        id="other-card-namespace-confirmation-dialog-message-card-count"
        value={cards.length}
        one={getNameForCard(cards[0])}
        other="# selected contacts"
      />
      &nbsp;
    </StyledBoldTextSpan>
  );
}
